import { AppBarSlotPortal, ArrowRightIcon, Stack } from "@datanomik/commons-ui";
import { ArrowLeftLine } from "@datanomik/commons-ui/icons";
import { IconButton, Typography } from "@mui/material";
import { Fragment } from "react";
import { useNavigateSearch } from "@/hooks/useNavigate";
import AnimatedEntrance from "@/views/common/AnimatedEntrance";

interface BreadcrumbItem {
  label: string;
  path?: string;
  description?: string;
  params?: Record<string, string>;
}

type CashPoolingBreadcrumbsProps = {
  items: BreadcrumbItem[];
};

export const CashPoolingBreadcrumbs = (props: CashPoolingBreadcrumbsProps) => {
  const { items } = props;
  const previousItems = items.slice(0, -1);
  const currentItem = items[items.length - 1];

  const navigateSearch = useNavigateSearch();

  const firstPreviousWithPath = previousItems.find((item) => item.path);

  const handleBack = () => {
    if (!firstPreviousWithPath) {
      return;
    }
    // Extract path starting from /cash-pooling
    const cashPoolingIndex = firstPreviousWithPath.path.indexOf("/cash-pooling");
    const relativePath =
      cashPoolingIndex !== -1
        ? firstPreviousWithPath.path.substring(cashPoolingIndex)
        : firstPreviousWithPath.path;
    navigateSearch(relativePath, firstPreviousWithPath.params);
  };

  return (
    <AppBarSlotPortal name="top-left">
      <Stack direction="row" spacing={1} alignItems="center">
        {/* Back button - shown when there are previous items */}
        {previousItems.length > 0 && firstPreviousWithPath && (
          <AnimatedEntrance delay={0} direction="left">
            <IconButton
              onClick={handleBack}
              size="small"
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "text.primary",
                },
              }}
            >
              <ArrowLeftLine />
            </IconButton>
          </AnimatedEntrance>
        )}

        <Stack spacing={0.5}>
          {/* Previous breadcrumb items */}

          {previousItems.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center">
              {previousItems.map((item, index) => (
                <Fragment key={item.path}>
                  <AnimatedEntrance delay={index * 0.1} direction="left">
                    <Typography
                      color="textSecondary"
                      fontSize="14px"
                      onClick={() => {
                        if (!item.path) {
                          return;
                        }
                        // Extract path starting from /cash-pooling
                        const cashPoolingIndex =
                          item.path.indexOf("/cash-pooling");
                        const relativePath =
                          cashPoolingIndex !== -1
                            ? item.path.substring(cashPoolingIndex)
                            : item.path;
                        navigateSearch(relativePath, item.params);
                      }}
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {item.label}
                    </Typography>
                  </AnimatedEntrance>
                  {index < previousItems.length - 1 && (
                    <ArrowRightIcon
                      sx={{
                        color: "text.secondary",
                        fontSize: 18,
                        paddingTop: "3px",
                      }}
                    />
                  )}
                </Fragment>
              ))}
            </Stack>
          )}

          {/* Current page label */}
          <AnimatedEntrance delay={previousItems.length * 0.1} direction="left">
            <Typography fontSize="16px" fontWeight={500}>
              {currentItem.label}
            </Typography>
          </AnimatedEntrance>
        </Stack>
      </Stack>
    </AppBarSlotPortal>
  );
};
